/**
 * Machine page › Illustration.
 *
 * The demo's `renderIll()` (line 2186) with its three views behind one mode
 * switch:
 *
 * | Mode | What | Available for |
 * |---|---|---|
 * | **360°** (default) | the studio-render turntable — drag to rotate, eight snapshot angles, Explode, a glow on the highlighted component, draggable callouts | every model (`ML-14`/`MT-50` share the `ML-21`/`MT-63` frames) |
 * | **3D** | the three.js model — orbit, zoom, Explode, click a component | loaders, trucks, jumbos, longhole drills |
 * | **2D** | the shaded vector drawing with a side / plan toggle, beside the side-view JPG | the same four classes |
 *
 * The demo shows only the turntable when a model has frames; the port keeps
 * the 2D and 3D views reachable behind the switch because the reviewers asked
 * for all three. Surface drills get 360° only — the demo has no drawing and no
 * 3D builder for them either.
 *
 * Beside the 360° and 3D views sits the component list, worst first. Pointing
 * at a row lights the component on the render; clicking a row, a callout, a
 * pin or a 3D part selects it, which (in `MachineScene`) filters the work
 * history and moves the reader there — the demo's own cross-highlight.
 *
 * The chosen mode is remembered for the session, so returning from the
 * history keeps the view the reader was in.
 *
 * UI layer.
 *
 * @see src/versions/v1/shared/viz/Turntable.tsx
 * @see src/versions/v1/shared/viz/Machine3D.tsx
 * @see docs/logic/fleet/machine-page.md
 */

import { useState } from 'react';
import { SurfacePageTabs } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import type { ComponentHealth } from '../../data/core';
import type { Machine } from '../../schema/machine';
import { ATTENTION_BANDS, CONDITION_BAND_LABEL, sideViewModel } from '../../schema/machine';
import { healthBand } from '../../shared/oem-engine';
import { useOemDocIdentity } from '../../shared/docs/useOemDocIdentity';
import { SideRender } from '../../shared/viz/SideRender';
import { hasMachine3D, Machine3D } from '../../shared/viz/Machine3D';
import {
  hasIllustration,
  MachineIllustration,
  type IllustrationView,
} from '../../shared/viz/MachineIllustration';
import { hasTurntable, Turntable } from '../../shared/viz/Turntable';
import { componentsBySeverity } from '../../shared/viz/Turntable.transform';

const VIEW_TABS = [
  { id: 'side' as const, label: 'Side' },
  { id: 'plan' as const, label: 'Plan' },
];

type IllustrationMode = '360' | '3d' | '2d';

const MODE_LABEL: Readonly<Record<IllustrationMode, string>> = {
  '360': '360°',
  '3d': '3D',
  '2d': '2D',
};

/** The demo's `ILLVIEW` global: the last mode picked, for the session. */
let lastMode: IllustrationMode | null = null;

export interface MachineIllustrationTabProps {
  machine: Machine;
  health: readonly ComponentHealth[];
  selected: string | null;
  onSelect: (component: string | null) => void;
}

export function MachineIllustrationTab({
  machine,
  health,
  selected,
  onSelect,
}: MachineIllustrationTabProps) {
  const drawable = hasIllustration(machine.cls);
  const modes: IllustrationMode[] = [
    ...(hasTurntable(machine.model) ? (['360'] as const) : []),
    ...(hasMachine3D(machine.cls) ? (['3d'] as const) : []),
    ...(drawable ? (['2d'] as const) : []),
  ];
  const [picked, setPicked] = useState<IllustrationMode | null>(lastMode);
  const mode: IllustrationMode = picked && modes.includes(picked) ? picked : (modes[0] ?? '2d');
  const pick = (m: IllustrationMode) => {
    lastMode = m;
    setPicked(m);
  };

  const [view, setView] = useState<IllustrationView>('side');
  const [preview, setPreview] = useState<string | null>(null);
  const flagged = health.filter((h) => ATTENTION_BANDS.includes(healthBand(h))).length;

  const modeTabs = modes.length > 1 ? modes.map((id) => ({ id, label: MODE_LABEL[id] })) : null;
  const clear = selected ? (
    <button
      type="button"
      onClick={() => onSelect(null)}
      className="text-xs text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
    >
      Clear selection
    </button>
  ) : null;

  const turntable = (
    <Turntable
      machine={machine}
      health={health}
      selected={preview ?? selected}
      onSelectComponent={onSelect}
    />
  );

  if (mode === '2d') {
    return (
      <Commentable name="MachineIllustration">
        <div className="space-y-3">
          {modeTabs ? (
            <SurfacePageTabs
              items={modeTabs}
              value={mode}
              onChange={pick}
              aria-label="Illustration mode"
            />
          ) : null}
          <TwoD
            machine={machine}
            health={health}
            selected={selected}
            onSelect={onSelect}
            view={view}
            setView={setView}
            flagged={flagged}
            clear={clear}
          />
        </div>
      </Commentable>
    );
  }

  return (
    <Commentable name="MachineIllustration">
      <div className="grid grid-cols-1 gap-6 @4xl:grid-cols-[minmax(0,1.618fr)_minmax(0,1fr)]">
        <section className="rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg">
          <div className="mb-3 flex items-center justify-between gap-3">
            {modeTabs ? (
              <SurfacePageTabs
                items={modeTabs}
                value={mode}
                onChange={pick}
                aria-label="Illustration mode"
              />
            ) : (
              <span className="text-xs text-muted">360° studio render</span>
            )}
            {clear}
          </div>
          {mode === '3d' ? (
            <Machine3D
              machine={machine}
              health={health}
              selected={preview ?? selected}
              onSelectComponent={onSelect}
              fallback={turntable}
            />
          ) : (
            turntable
          )}
          <p className="mt-2 flex flex-wrap justify-between gap-2 text-[11px] text-muted">
            <span>
              {mode === '3d'
                ? '3D model · drag to orbit · wheel to zoom · Explode separates the component groups'
                : '360° studio render · drag to rotate, click a snapshot below, Explode separates the component groups'}
            </span>
            <span>{flagged} flagged · click a marker or a component for its history</span>
          </p>
        </section>

        <ComponentList
          machineId={machine.machine_id}
          health={health}
          selected={selected}
          onPreview={setPreview}
          onSelect={onSelect}
        />
      </div>
    </Commentable>
  );
}

/** The 2D drawing beside the side-view JPG — the tab as it was before the 360° view. */
function TwoD({
  machine,
  health,
  selected,
  onSelect,
  view,
  setView,
  flagged,
  clear,
}: MachineIllustrationTabProps & {
  view: IllustrationView;
  setView: (v: IllustrationView) => void;
  flagged: number;
  clear: React.ReactNode;
}) {
  const oem = useOemDocIdentity();
  return (
    <div className="grid grid-cols-1 gap-6 @4xl:grid-cols-[minmax(0,1.618fr)_minmax(0,1fr)]">
      <section className="rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg">
        <div className="mb-3 flex items-center justify-between gap-3">
          <SurfacePageTabs
            items={VIEW_TABS}
            value={view}
            onChange={setView}
            aria-label="Illustration view"
          />
          {clear}
        </div>
        <MachineIllustration
          machineId={machine.machine_id}
          model={machine.model}
          cls={machine.cls}
          view={view}
          health={health}
          selected={selected}
          onSelect={onSelect}
        />
        <p className="mt-2 flex flex-wrap justify-between gap-2 text-[11px] text-muted">
          <span>
            {view === 'side' ? 'Side view' : 'Plan view'} · click a marker for the component
          </span>
          <span>{flagged} flagged</span>
        </p>
      </section>

      <figure className="rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg">
        <SideRender
          model={machine.model}
          alt={`${machine.model} side elevation, studio render`}
          className="mx-auto w-full rounded-lg"
        />
        <figcaption className="mt-2 text-[11px] text-muted">
          Side elevation, studio render of the {oem.short} {machine.model}.
          {sideViewModel(machine.model) !== machine.model
            ? ` The ${machine.model} shares the ${sideViewModel(machine.model)} body.`
            : ''}
        </figcaption>
      </figure>
    </div>
  );
}

/**
 * Demo line 2180. `#compl-m` — the machine's components, worst first. Hover
 * or focus previews the glow; click selects.
 */
function ComponentList({
  machineId,
  health,
  selected,
  onPreview,
  onSelect,
}: {
  machineId: string;
  health: readonly ComponentHealth[];
  selected: string | null;
  onPreview: (component: string | null) => void;
  onSelect: (component: string | null) => void;
}) {
  const rows = componentsBySeverity(health);
  return (
    <section
      className="rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg"
      aria-label={`${machineId} component condition`}
      onMouseLeave={() => onPreview(null)}
    >
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">
        {machineId} · component condition{' '}
        <span className="font-normal normal-case">score /100</span>
      </h3>
      <ul className="flex flex-col">
        {rows.map((h) => {
          const band = healthBand(h);
          const on = selected === h.component;
          return (
            <li key={h.component}>
              <button
                type="button"
                aria-pressed={on}
                onMouseEnter={() => onPreview(h.component)}
                onFocus={() => onPreview(h.component)}
                onBlur={() => onPreview(null)}
                onClick={() => onSelect(h.component)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
                  'hover:bg-[var(--glass-surface-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
                  on && 'bg-[var(--accent-soft)]',
                )}
              >
                <span
                  aria-hidden="true"
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: `var(--band-${band})` }}
                />
                <span className="flex-1 truncate text-zinc-900 dark:text-zinc-100">
                  {h.component}
                </span>
                <span className="mono text-muted">{h.health_score}</span>
                <span className={`band-pill band-${band} shrink-0`}>
                  {CONDITION_BAND_LABEL[band]}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
